import React, { useState, useEffect, useRef, useCallback } from 'react';
import Toast from './Toast';
import MD from './MD';
import ReportViewer from './ReportViewer';
import useToast from '../hooks/useToast';

/**
 * Wissens-Layer (Achse 2): die Recherche-Reports durchsuchen und befragen.
 * - 🔍 Suchen: FTS-Volltext mit Snippets → Report öffnen
 * - 💬 Fragen: RAG — Antwort strikt aus den eigenen Reports, mit [R<id>]-Zitaten
 * Alles lokal (Embeddings + LLM auf dem Mac Mini).
 */

// Snippets enthalten rohen Report-Text mit <mark>-Highlights → alles escapen,
// nur <mark> wiederherstellen (gleiches XSS-Schutz-Muster wie SearchBar).
function safeSnippet(s) {
  const esc = (s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return esc
    .replace(/&lt;mark&gt;/g, '<mark>')
    .replace(/&lt;\/mark&gt;/g, '</mark>');
}

const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('de-DE') : '';

export default function KnowledgeView({ authToken, onLogout, onHelp }) {
  const [mode, setMode]         = useState('ask');       // 'ask' | 'search' | 'map' | 'drift'
  const [q, setQ]               = useState('');
  const [results, setResults]   = useState([]);          // FTS/semantic-Treffer
  const [answer, setAnswer]     = useState(null);        // {answer, sources}
  const [mapData, setMapData]   = useState(null);        // Themen-Landkarte
  const [drift, setDrift]       = useState(null);        // Drift-Radar
  const [busy, setBusy]         = useState(false);
  const [status, setStatus]     = useState(null);        // Index-Status
  const [viewReport, setViewReport] = useState(null);
  const [toasts, pushToast]     = useToast();
  const pollRef = useRef({ timer: null, token: 0 });

  const auth = { Authorization: `Bearer ${authToken}` };

  const loadStatus = useCallback(() => {
    fetch('/api/knowledge/status', { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => {
        if (r.status === 401) { onLogout?.(); return null; }
        return r.json();
      })
      .then(d => { if (d) setStatus(d); })
      .catch(() => {});
  }, [authToken, onLogout]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => () => {   // Poll-Cleanup beim Unmount
    pollRef.current.token++;
    if (pollRef.current.timer) clearTimeout(pollRef.current.timer);
  }, []);

  const runSearch = async () => {
    if (!q.trim()) return;
    setBusy(true); setAnswer(null);
    try {
      const r = await fetch(`/api/knowledge/search?q=${encodeURIComponent(q.trim())}&mode=fts`, { headers: auth });
      const d = await r.json();
      if (!r.ok) { pushToast(d.error || 'Suche fehlgeschlagen', 'error'); return; }
      setResults(d.results || []);
      if ((d.results || []).length === 0) pushToast('Keine Treffer im Report-Korpus', 'info');
    } catch { pushToast('Netzwerkfehler', 'error'); }
    finally { setBusy(false); }
  };

  const pollAsk = (askId) => {
    pollRef.current.token++;
    const myToken = pollRef.current.token;
    let tries = 0;
    const tick = async () => {
      if (myToken !== pollRef.current.token) return;
      tries++;
      const r = await fetch(`/api/knowledge/ask/${askId}`, { headers: auth }).catch(() => null);
      if (myToken !== pollRef.current.token) return;
      const d = r && r.ok ? await r.json().catch(() => null) : null;
      if (d && d.status === 'done') { setAnswer(d); setBusy(false); return; }
      if (d && d.status === 'error') { pushToast(d.error || 'Antwort fehlgeschlagen', 'error'); setBusy(false); return; }
      if (!d && r && r.status === 404) { pushToast('Anfrage verloren (Neustart?) — bitte erneut fragen', 'error'); setBusy(false); return; }
      // 429 (Rate-Limit des Status-Endpoints) ist transient — einfach weiterpollen
      if (tries < 60) pollRef.current.timer = setTimeout(tick, 2500);   // bis ~2.5 min
      else { pushToast('Antwort dauert ungewöhnlich lange — bitte erneut versuchen', 'info'); setBusy(false); }
    };
    pollRef.current.timer = setTimeout(tick, 2500);
  };

  const runAsk = async () => {
    if (!q.trim()) return;
    setBusy(true); setResults([]); setAnswer(null);
    try {
      const r = await fetch('/api/knowledge/ask', {
        method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q.trim() }),
      });
      const d = await r.json();
      if (!r.ok) { pushToast(d.error || 'Frage nicht möglich', 'error'); setBusy(false); return; }
      pollAsk(d.ask_id);
    } catch { pushToast('Netzwerkfehler', 'error'); setBusy(false); }
  };

  const submit = (e) => { e.preventDefault(); mode === 'ask' ? runAsk() : runSearch(); };

  // Landkarte / Drift: beim Modus-Wechsel IMMER frisch laden (Audit v3: der alte
  // null-Gate-Cache blieb nach Kombi-Läufen stale). r.ok + 401 werden geprüft —
  // vorher landete {error:…} in mapData und die View starb in der ErrorBoundary.
  const _fetchChecked = (url) =>
    fetch(url, { headers: auth }).then(r => {
      if (r.status === 401) { pushToast('Sitzung abgelaufen', 'error'); onLogout?.(); throw new Error('401'); }
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    });

  const switchMode = (m) => {
    setMode(m);
    if (m === 'map') {
      setMapData(null);
      _fetchChecked('/api/knowledge/map')
        .then(d => setMapData(Array.isArray(d.nodes) ? d : { nodes: [], edges: [] }))
        .catch(() => { setMapData({ nodes: [], edges: [] }); pushToast('Landkarte nicht ladbar', 'error'); });
    }
    if (m === 'drift') {
      setDrift(null);
      _fetchChecked('/api/knowledge/drift')
        .then(d => setDrift(d.topics || []))
        .catch(() => { setDrift([]); pushToast('Drift-Radar nicht ladbar', 'error'); });
    }
  };

  const shortTopic = t => (t || '').split(/[:—]/)[0].trim().slice(0, 30);

  const reindex = async () => {
    const r = await fetch('/api/knowledge/reindex', { method: 'POST', headers: auth }).catch(() => null);
    if (r && (r.ok || r.status === 202)) {
      pushToast('Indexierung läuft im Hintergrund — Status aktualisiert sich', 'info');
      setTimeout(loadStatus, 5000); setTimeout(loadStatus, 20000); setTimeout(loadStatus, 60000);
    } else pushToast('Indexierung nicht möglich', 'error');
  };

  const indexed = status ? `${status.reports_indexed}/${status.reports_total}` : '…';

  return (
    <div className="knowledge-view">
      <Toast messages={toasts} />
      {viewReport !== null && (
        <ReportViewer reportId={viewReport} authToken={authToken} onClose={() => setViewReport(null)} />
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">Wissen</h1>
          <p className="page-sub">Frag deine Recherche — {status?.reports_total ?? '…'} Reports, lokal durchsuchbar &amp; befragbar</p>
        </div>
        {onHelp && (
          <button className="btn-ghost-sm help-btn"
            onClick={() => onHelp(mode === 'map' || mode === 'drift' ? 'landkarte-drift' : 'wissen')}
            title="Hilfe zu dieser Ansicht öffnen">❓</button>
        )}
        <div className="knowledge-index-chip" title="Wie viele Reports für die semantische Suche indexiert sind">
          🧠 {indexed} indexiert
          {status && status.reports_indexed < status.reports_total && (
            <button className="btn-ghost-sm" onClick={reindex} title="Fehlende Reports jetzt indexieren (läuft im Hintergrund)">↻ nachziehen</button>
          )}
        </div>
      </div>

      <form onSubmit={submit} className="knowledge-form">
        <div className="knowledge-mode-toggle">
          <button type="button" className={mode === 'ask' ? 'knowledge-mode-on' : ''}
            onClick={() => switchMode('ask')} title="Frage stellen — Antwort mit Zitaten aus deinen Reports">💬 Fragen</button>
          <button type="button" className={mode === 'search' ? 'knowledge-mode-on' : ''}
            onClick={() => switchMode('search')} title="Volltextsuche über alle Reports">🔍 Suchen</button>
          <button type="button" className={mode === 'map' ? 'knowledge-mode-on' : ''}
            onClick={() => switchMode('map')} title="Themen-Landkarte: welche Themen sind verbunden, welche isoliert">🗺 Landkarte</button>
          <button type="button" className={mode === 'drift' ? 'knowledge-mode-on' : ''}
            onClick={() => switchMode('drift')} title="Drift-Radar: welche Begriffe je Thema neu auftauchen oder abflauen">📈 Drift</button>
        </div>
        {(mode === 'ask' || mode === 'search') && (
          <>
            <input
              className="field-input knowledge-input"
              type="text"
              placeholder={mode === 'ask' ? 'z.B. Was wissen wir über Reward Hacking bei KI-Agenten?' : 'Suchbegriff, z.B. Mitbestimmung KI'}
              value={q}
              onChange={e => setQ(e.target.value)}
              maxLength={500}
              autoFocus
            />
            <button type="submit" className="btn-primary" disabled={busy || !q.trim()}
              title={mode === 'ask' ? 'Antwort aus deinen Reports generieren (~30–60s, lokal)' : 'Reports durchsuchen'}>
              {busy ? '…' : (mode === 'ask' ? 'Fragen' : 'Suchen')}
            </button>
          </>
        )}
      </form>

      {mode === 'map' && (
        <div className="knowledge-map">
          {mapData === null && <div className="knowledge-pending">Lade Themen-Landkarte…</div>}
          {mapData && (() => {
            const topicOf = {};
            mapData.nodes.forEach(n => { topicOf[n.id] = n.topic; });
            const edgesFor = id => mapData.edges.filter(e => e.a === id || e.b === id);
            return (
              <>
                <p className="knowledge-hint">Verbindungen entstehen durch ⊕ Kombinationen und Ideen, die Reports mehrerer Themen verschmelzen. 🏝-Themen sind unerschlossene Kombi-Chancen.</p>
                <div className="map-grid">
                  {mapData.nodes.map(n => (
                    <div key={n.id} className={`map-node ${n.isolated ? 'map-node-isolated' : ''}`}>
                      <div className="map-node-top">
                        <span className="map-node-topic" title={n.topic}>{n.isolated ? '🏝 ' : ''}{shortTopic(n.topic)}</span>
                        <span className="map-node-meta">{n.report_count} Reports</span>
                      </div>
                      {n.isolated ? (
                        <div className="map-node-iso-hint">noch mit keinem Thema verbunden — Kandidat für ⊕ Kombinieren</div>
                      ) : (
                        <div className="map-node-edges">
                          {edgesFor(n.id).map((e, i) => {
                            const other = e.a === n.id ? e.b : e.a;
                            const parts = [];
                            if (e.kombi) parts.push(`${e.kombi}× Kombi`);
                            if (e.idee) parts.push(`${e.idee}× Idee`);
                            return (
                              <span key={i} className="map-edge-chip" title={`Verbunden mit „${topicOf[other]}" über ${parts.join(' + ')}`}>
                                ↔ {shortTopic(topicOf[other])} <em>({parts.join(', ')})</em>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {mode === 'drift' && (
        <div className="knowledge-drift">
          {drift === null && <div className="knowledge-pending">Lade Drift-Radar…</div>}
          {Array.isArray(drift) && drift.length === 0 && (
            <div className="empty-state-card"><div className="empty-icon">📈</div>
              <p className="empty-title">Noch kein Drift messbar</p>
              <p className="empty-sub">Braucht mindestens 3 Reports je Thema — der Radar vergleicht den neuesten Lauf mit den vorherigen.</p></div>
          )}
          {Array.isArray(drift) && drift.length > 0 && (
            <>
              <p className="knowledge-hint">Heuristik auf signifikanten Begriffen (Template-Wörter gefiltert): 📈 neu im letzten Lauf · 📉 vorher stabil, jetzt weg. Ein Radar, kein Beweis.</p>
              {drift.map(t => (
                <div key={t.job_id} className="drift-card">
                  <div className="drift-card-top">
                    <span className="drift-topic" title={t.topic}>{shortTopic(t.topic)}</span>
                    <span className="drift-meta">letzter Lauf {fmtDate(t.last_report_at)} · vs. {t.compared_reports} Vor-Report{t.compared_reports > 1 ? 's' : ''}</span>
                  </div>
                  {t.new_terms.length > 0 && (
                    <div className="drift-row">📈 {t.new_terms.map(w => <span key={w} className="drift-term drift-new">{w}</span>)}</div>
                  )}
                  {t.faded_terms.length > 0 && (
                    <div className="drift-row">📉 {t.faded_terms.map(w => <span key={w} className="drift-term drift-faded">{w}</span>)}</div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {busy && mode === 'ask' && (
        <div className="knowledge-pending">💬 Antwort wird aus deinen Reports erarbeitet (lokal, ~30–60s)…</div>
      )}

      {(mode === 'ask') && answer && (
        <div className="knowledge-answer">
          <div className="knowledge-answer-body"><MD content={answer.answer} /></div>
          {(answer.sources || []).length > 0 && (
            <div className="knowledge-sources">
              <div className="knowledge-sources-label">Belege aus deinen Reports:</div>
              {answer.sources.map((s, i) => (
                <button key={i} className="knowledge-source" onClick={() => setViewReport(s.report_id)}
                  title={`Report #${s.report_id} öffnen`}>
                  <span className="knowledge-source-ref">[R{s.report_id}]</span>
                  <span className="knowledge-source-topic">{s.topic}</span>
                  <span className="knowledge-source-date">{fmtDate(s.created_at)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {(mode === 'search') && results.length > 0 && (
        <div className="knowledge-results">
          {results.map((r, i) => (
            <button key={i} className="knowledge-result" onClick={() => setViewReport(r.report_id)}
              title={`Report #${r.report_id} öffnen`} aria-label={`Report öffnen: ${r.topic}`}>
              <div className="knowledge-result-top">
                <span className="knowledge-result-topic">{r.topic}</span>
                <span className="knowledge-result-date">#{r.report_id} · {fmtDate(r.created_at)}</span>
              </div>
              <div className="knowledge-result-snippet" dangerouslySetInnerHTML={{ __html: safeSnippet(r.snippet) }} />
            </button>
          ))}
        </div>
      )}

      {(mode === 'ask' || mode === 'search') && !busy && !answer && results.length === 0 && (
        <div className="empty-state-card">
          <div className="empty-icon">🧠</div>
          <p className="empty-title">Dein Recherche-Gedächtnis</p>
          <p className="empty-sub">
            Stell eine Frage — die Antwort kommt ausschließlich aus deinen verifizierten
            Reports, mit klickbaren Belegen. Oder durchsuche den Volltext.
          </p>
        </div>
      )}
    </div>
  );
}
